import { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowUpCircle, Lightbulb, MessageSquareReply, Plug, SendHorizontal, Sparkles } from "lucide-react";
import { useAppServices } from "../../hook/services";
import { cx, formatDate } from "../../utils";
import Input, { Label, Textarea } from "../../components/ui/Input";
import { useToast } from "../../components/ui/Toast";

/**
 * Feature requests for this app, from this account.
 *
 * The form sends a request to the team; the list underneath shows what this
 * account has already asked for, where each request stands, and any reply.
 * Who sent it comes from the CRM's sign-in, so nobody has to type their name.
 */

const CATEGORIES = [
  { id: "New feature", label: "New feature", icon: Sparkles, hint: "Something the app can't do yet" },
  { id: "Improvement", label: "Improvement", icon: ArrowUpCircle, hint: "Make something better" },
  { id: "New integration", label: "New integration", icon: Plug, hint: "Connect another tool" },
];

// Worded for the person who asked, not for the team triaging it.
const STATUS = {
  new: { label: "Received", cls: "bg-ink-100 text-ink-600 ring-ink-200/80", dot: "bg-ink-400" },
  under_review: { label: "Under review", cls: "bg-amber-50 text-amber-700 ring-amber-200/80", dot: "bg-amber-500" },
  planned: { label: "Planned", cls: "bg-violet-50 text-violet-700 ring-violet-200/70", dot: "bg-violet-500" },
  in_progress: { label: "In progress", cls: "bg-brand-50 text-brand-700 ring-brand-200/70", dot: "bg-brand-500" },
  done: { label: "Shipped", cls: "bg-emerald-50 text-emerald-700 ring-emerald-200/80", dot: "bg-emerald-500" },
  declined: { label: "Not planned", cls: "bg-rose-50 text-rose-700 ring-rose-200/80", dot: "bg-rose-500" },
};

// The same limits the backend enforces (routes/featureRequests/services.js).
const TITLE_MIN = 3;
const TITLE_MAX = 120;
const DESCRIPTION_MIN = 10;
const DESCRIPTION_MAX = 4000;

const FieldHint = ({ id, length, min, max, checked }) => {
  const short = length < min;
  const missing = min - length;
  return (
    <div className="mt-1.5 flex items-start justify-between gap-3 text-[11.5px]">
      <p id={id} className={cx(short && checked ? "font-medium text-rose-600" : "text-ink-400")} role={short && checked ? "alert" : undefined}>
        {short && checked
          ? `Add at least ${missing} more character${missing === 1 ? "" : "s"} — the minimum is ${min}.`
          : `At least ${min} characters.`}
      </p>
      <span className={cx("shrink-0 tabular-nums", short ? "text-ink-400" : "text-emerald-600")}>
        {length}/{max}
      </span>
    </div>
  );
};

const RequestCard = ({ request }) => {
  const s = STATUS[request.status] || STATUS.new;
  const Icon = (CATEGORIES.find((c) => c.id === request.category) || CATEGORIES[0]).icon;
  return (
    <li className="animate-fade-in rounded-xl border border-ink-200/70 bg-white p-4 shadow-sm">
      <div className="flex items-start gap-3">
        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-brand-50 text-brand-600 ring-1 ring-brand-200/60">
          <Icon size={16} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <p className="text-[14px] font-semibold text-ink-900">{request.title}</p>
            <span className={cx("inline-flex shrink-0 items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1", s.cls)}>
              <span className={cx("h-1.5 w-1.5 rounded-full", s.dot)} />
              {s.label}
            </span>
          </div>
          <p className="mt-0.5 text-[11.5px] text-ink-400">
            {request.category} · {formatDate(request.createdAt)}
          </p>
          {request.description && <p className="mt-2 whitespace-pre-line text-[13px] leading-relaxed text-ink-600">{request.description}</p>}

          {request.response && (
            <div className="mt-3 flex gap-2.5 rounded-xl bg-brand-subtle px-3.5 py-3 ring-1 ring-brand-200/60">
              <MessageSquareReply size={15} className="mt-0.5 shrink-0 text-brand-600" aria-hidden />
              <div className="min-w-0">
                <p className="text-[11.5px] font-semibold text-brand-700">
                  Reply from the team
                  {request.respondedAt && <span className="ml-1.5 font-normal text-brand-600/80">{formatDate(request.respondedAt)}</span>}
                </p>
                <p className="mt-1 whitespace-pre-line text-[13px] leading-relaxed text-ink-700">{request.response}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </li>
  );
};

const FeatureRequestsPanel = ({ app, appId, locationId, companyId, user }) => {
  const { featureRequests } = useAppServices();
  const toast = useToast();

  const [category, setCategory] = useState(CATEGORIES[0].id);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [sending, setSending] = useState(false);
  const [checked, setChecked] = useState(false);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);

  const query = useMemo(
    () => new URLSearchParams({ appId, ...(locationId && { locationId }), ...(companyId && { companyId }) }).toString(),
    [appId, locationId, companyId],
  );

  const load = useCallback(async () => {
    const { response } = await featureRequests.List(query);
    setList(Array.isArray(response) ? response : []);
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  useEffect(() => {
    load();
  }, [load]);

  const titleLen = title.trim().length;
  const descriptionLen = description.trim().length;
  const titleShort = titleLen < TITLE_MIN;
  const descriptionShort = descriptionLen < DESCRIPTION_MIN;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (sending) return;
    // The button stays clickable; a short field says why instead.
    if (titleShort || descriptionShort) {
      setChecked(true);
      document.getElementById(titleShort ? "fr-title" : "fr-description")?.focus();
      return;
    }

    setSending(true);
    const { response, error } = await featureRequests.Create({
      appId,
      locationId: locationId || undefined,
      companyId: companyId || undefined,
      category,
      title,
      description,
      submittedBy: user ? { userId: user.userId, userName: user.userName, email: user.email, role: user.role } : undefined,
    });
    setSending(false);

    if (error) {
      toast(error?.message || "Could not send your request", "error");
      return;
    }
    toast("Request sent — thank you");
    setChecked(false);
    setTitle("");
    setDescription("");
    setCategory(CATEGORIES[0].id);
    if (response) setList((prev) => [response, ...prev]);
  };

  return (
    <div className="grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,1fr)_20rem] 2xl:grid-cols-[minmax(0,1fr)_24rem]">
      <div className="min-w-0 space-y-5">
        <form onSubmit={onSubmit} className="overflow-hidden rounded-2xl border border-ink-200/70 bg-white shadow-card">
          <header className="flex items-start gap-4 border-b border-ink-150 bg-brand-subtle px-6 py-5">
            <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-gradient-to-br from-brand-500 to-violet-600 text-white shadow-brand-glow">
              <Lightbulb size={20} />
            </span>
            <div className="min-w-0">
              <h2 className="text-[16px] font-semibold tracking-tight text-ink-900">Shape what we build next</h2>
              <p className="mt-0.5 text-[13px] text-ink-600">
                Tell us what would make {app.appName} more useful. Every request is read by the team that builds it.
              </p>
            </div>
          </header>

          <div className="space-y-6 px-6 py-6">
            <div>
              <Label>What kind of request is it?</Label>
              <div className="mt-2 grid gap-2 sm:grid-cols-3" role="radiogroup" aria-label="Request type">
                {CATEGORIES.map(({ id, label, icon: Icon, hint }) => {
                  const on = category === id;
                  return (
                    <button
                      key={id}
                      type="button"
                      role="radio"
                      aria-checked={on}
                      onClick={() => setCategory(id)}
                      className={cx(
                        "flex items-start gap-3 rounded-xl border p-3 text-left transition-all",
                        "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500",
                        on ? "border-brand-300 bg-brand-50 shadow-glow" : "border-ink-200 bg-white hover:border-ink-300 hover:bg-ink-50",
                      )}
                    >
                      <Icon size={17} className={cx("mt-0.5 shrink-0", on ? "text-brand-600" : "text-ink-400")} />
                      <span>
                        <span className={cx("block text-[13px] font-semibold", on ? "text-brand-700" : "text-ink-800")}>{label}</span>
                        <span className="block text-[11.5px] text-ink-500">{hint}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <Label htmlFor="fr-title" required>Title</Label>
              <Input
                id="fr-title"
                className="mt-1.5"
                value={title}
                maxLength={TITLE_MAX}
                invalid={checked && titleShort}
                aria-describedby="fr-title-hint"
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Send pictures from the conversation"
              />
              <FieldHint id="fr-title-hint" length={titleLen} min={TITLE_MIN} max={TITLE_MAX} checked={checked} />
            </div>

            <div>
              <Label htmlFor="fr-description" required>Details</Label>
              <Textarea
                id="fr-description"
                className="mt-1.5"
                rows={5}
                value={description}
                maxLength={DESCRIPTION_MAX}
                invalid={checked && descriptionShort}
                aria-describedby="fr-description-hint"
                onChange={(e) => setDescription(e.target.value)}
                placeholder="What would you like it to do, and what would it help you with?"
              />
              <FieldHint id="fr-description-hint" length={descriptionLen} min={DESCRIPTION_MIN} max={DESCRIPTION_MAX} checked={checked} />
            </div>
          </div>

          <footer className="flex flex-wrap items-center justify-end gap-3 border-t border-ink-150 bg-ink-50/50 px-6 py-3.5">
            {user?.userName && (
              <p className="mr-auto text-[12px] text-ink-500">
                Sent as <span className="font-medium text-ink-700">{user.userName}</span>
                {user.email ? ` · ${user.email}` : ""}
              </p>
            )}
            <button
              type="submit"
              disabled={sending}
              className="inline-flex h-9 items-center gap-1.5 rounded-xl bg-gradient-to-br from-brand-500 to-violet-600 px-4 text-[13px] font-semibold text-white shadow-brand-glow transition-all hover:brightness-110 disabled:opacity-60"
            >
              {sending ? "Sending…" : "Send request"} <SendHorizontal size={14} />
            </button>
          </footer>
        </form>

        <section>
          <div className="mb-3 flex items-baseline justify-between px-1">
            <h3 className="text-[15px] font-semibold tracking-tight text-ink-900">Your requests</h3>
            {list.length > 0 && <span className="text-[12px] text-ink-500">{list.length} sent</span>}
          </div>
          {loading ? (
            <div className="space-y-3">
              {[0, 1].map((i) => (
                <div key={i} className="h-24 animate-pulse rounded-xl bg-white ring-1 ring-ink-200/70" />
              ))}
            </div>
          ) : list.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-ink-200 bg-white/60 px-6 py-10 text-center">
              <span className="mx-auto grid h-11 w-11 place-items-center rounded-2xl bg-brand-50 text-brand-600 ring-1 ring-brand-200/60">
                <Lightbulb size={18} />
              </span>
              <p className="mt-3 text-[13.5px] font-semibold text-ink-800">No requests yet</p>
              <p className="mt-0.5 text-[12.5px] text-ink-500">Anything you send shows up here, with its status and our reply.</p>
            </div>
          ) : (
            <ul className="space-y-3">
              {list.map((r) => (
                <RequestCard key={r._id} request={r} />
              ))}
            </ul>
          )}
        </section>
      </div>

      <aside className="space-y-5 lg:sticky lg:top-20 lg:self-start">
        <section className="rounded-2xl border border-ink-200/70 bg-white p-5 shadow-card">
          <h3 className="text-[13.5px] font-semibold text-ink-900">What happens next</h3>
          <ol className="mt-4 space-y-0">
            {[
              ["Received", "The team reads every request.", STATUS.new],
              ["Under review", "We check how it fits the app.", STATUS.under_review],
              ["Planned → In progress", "It's on the roadmap, then being built.", STATUS.planned],
              ["Shipped", "It's live — often with a note on how to use it.", STATUS.done],
            ].map(([t, d, s], i, all) => (
              <li key={t} className="relative flex gap-3 pb-4 last:pb-0">
                {i < all.length - 1 && <span className="absolute left-[5px] top-4 h-full w-px bg-ink-200" aria-hidden />}
                <span className={cx("relative mt-1.5 h-[11px] w-[11px] shrink-0 rounded-full ring-4 ring-white", s.dot)} />
                <div>
                  <p className="text-[13px] font-medium text-ink-800">{t}</p>
                  <p className="text-[12px] leading-relaxed text-ink-500">{d}</p>
                </div>
              </li>
            ))}
          </ol>
        </section>
      </aside>
    </div>
  );
};

export default FeatureRequestsPanel;
